import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { FEATURE_SELECTOR_ITEMS } from "./featureData";
import { smoothScrollTo } from "../../lib/scroll";

export const FeatureSelector = () => {
  const [active, setActive] = useState(FEATURE_SELECTOR_ITEMS[0][1]);
  const [open, setOpen] = useState(false);
  const navRef = useRef(null);
  const railRef = useRef(null);
  const navigationLock = useRef(0);
  const activeLabel = FEATURE_SELECTOR_ITEMS.find(([, id]) => id === active)?.[0] || "AI";

  useEffect(() => {
    const sections = FEATURE_SELECTOR_ITEMS.map(([, id]) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver(() => {
      if (Date.now() < navigationLock.current) return;
      const marker = window.innerHeight * .36;
      const visible = sections.find((section) => {
        const bounds = section.getBoundingClientRect();
        return bounds.top <= marker && bounds.bottom >= marker;
      });
      if (visible) setActive(visible.id);
    }, { threshold: [0, .1, .5] });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    const node = rail?.querySelector(`[data-feature-id="${active}"]`);
    if (!rail || !node || rail.clientHeight === 0) return;
    rail.scrollTo({ top: node.offsetTop - rail.clientHeight / 2 + node.clientHeight / 2, behavior: "smooth" });
  }, [active, open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "pointerdown" && navRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", close);
    document.addEventListener("pointerdown", close);
    return () => { document.removeEventListener("keydown", close); document.removeEventListener("pointerdown", close); };
  }, [open]);

  const jumpTo = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    navigationLock.current = Date.now() + 900;
    setActive(id);
    setOpen(false);
    smoothScrollTo(window.scrollY + target.getBoundingClientRect().top - 96, { immediate: true });
  };

  return (
    <nav ref={navRef} id="feature-selector" className="feature-selector" aria-label="Feature sections" data-testid="features-sticky-selector">
      <button type="button" className="feature-selector-toggle" aria-expanded={open} aria-controls="feature-selector-menu" onClick={() => setOpen((value) => !value)} data-testid="features-selector-toggle">
        <span><small>FEATURE INDEX</small><strong data-testid="features-selector-current-label">{activeLabel}</strong></span>
        <ChevronDown aria-hidden="true" />
      </button>
      <div id="feature-selector-menu" ref={railRef} className={`feature-selector-rail ${open ? "is-open" : ""}`} data-testid="features-selector-menu">
        <p className="feature-selector-heading" data-testid="features-selector-heading"><small>EXPLORE</small><strong>FEATURE INDEX</strong></p>
        {FEATURE_SELECTOR_ITEMS.map(([label, id], index) => (
          <button key={id} type="button" data-feature-id={id} data-testid={`feature-selector-${id}`} aria-current={active === id ? "true" : undefined} onClick={() => jumpTo(id)}>
            <span>{String(index + 1).padStart(2, "0")}</span><strong>{label}</strong>
          </button>
        ))}
      </div>
    </nav>
  );
};